import React, { useMemo, useState } from "react";
import { AudioLines, Check, Download, FileText, Headphones, LoaderCircle, RefreshCw, Share2, Sparkles } from "lucide-react";
import useBuilderPodcastFiles, { PODCAST_FILES } from "./useBuilderPodcastFiles";
import { podcastListenPath } from "../../../features/podcastSharing/podcastShare";
import PodcastShareDialog from "../../../features/podcastSharing/PodcastShareDialog";
import { trackPodcastAction } from "../../../features/podcastSharing/podcastAnalytics";
import "./MBPodcastsTab.css";

function runTime(run) {
  const timestamp = run?.createdAt || run?.created_at || run?.submittedAt;
  return timestamp?.toMillis?.() || (timestamp?.seconds ? timestamp.seconds * 1000 : 0);
}

function statusDisplay(status) {
  const normalized = String(status || "").toLowerCase();
  if (["complete", "completed", "succeeded", "success"].includes(normalized)) return { label: "Ready", kind: "ready" };
  if (["failed", "error", "cancelled", "canceled"].includes(normalized)) return { label: "Error", kind: "failed" };
  if (["submitting", "submitted", "running", "queued", "pending"].includes(normalized)) return { label: "In progress", kind: "running" };
  return { label: status || "Processing", kind: "running" };
}

function DownloadFileCard({ label, description, name, url }) {
  return <div className={`mb-podcastFile ${url ? "isReady" : ""}`}>
    <span className="mb-podcastFileIcon"><FileText size={17} /></span>
    <span className="mb-podcastFileInfo"><strong>{label}</strong><small>{url ? description : "Waiting for file"}</small></span>
    {url ? <a href={url} target="_blank" rel="noreferrer" download={name} aria-label={`Download ${label.toLowerCase()}`}><Download size={17} /></a> : <span className="mb-podcastUnavailable" aria-label={`${label} pending`}>· · ·</span>}
  </div>;
}

export default function MBPodcastsTab({ builderId, runs = [] }) {
  const { urls: fileUrls, loading: loadingFiles, error: fileError, refresh: refreshFiles } = useBuilderPodcastFiles(builderId, runs);
  const sortedRuns = useMemo(() => [...runs].sort((a, b) => runTime(b) - runTime(a)), [runs]);
  const [sharingRunId, setSharingRunId] = useState("");
  const shareUrl = sharingRunId && typeof window !== "undefined"
    ? `${window.location.origin}${podcastListenPath(builderId, sharingRunId)}` : "";

  return <section className="mb-podcastsPanel">
    <header className="mb-podcastsHeader">
      <div className="mb-podcastsTitleGroup">
        <div className="mb-podcastsEyebrow"><Sparkles size={14} /> PODCAST STUDIO</div>
        <h2>Your podcasts</h2>
        <p>Listen to episodes created in chat and pick up their files as they become ready.</p>
      </div>
      <button className="mb-podcastsRefresh" type="button" onClick={refreshFiles} disabled={loadingFiles}>
        <RefreshCw size={16} className={loadingFiles ? "isSpinning" : ""} />
        {loadingFiles ? "Checking…" : "Refresh files"}
      </button>
    </header>
    {fileError && <p className="mb-podcastsError" role="alert">Could not check some podcast files: {fileError}</p>}
    <div className="mb-podcastsList">
      {sortedRuns.map((run, index) => {
        const runId = String(run.runId || run.id);
        const status = String(run.status || "Unknown");
        const displayStatus = statusDisplay(status);
        const date = runTime(run);
        const audioUrl = fileUrls[run.id]?.mp3Path;
        const readyCount = PODCAST_FILES.filter((file) => !!fileUrls[run.id]?.[file.key]).length;
        return <article className="mb-podcastCard" key={run.id}>
          <div className="mb-podcastCardHeader">
            <div className="mb-podcastArtwork" aria-hidden="true"><AudioLines size={32} strokeWidth={1.8} /></div>
            <div className="mb-podcastIdentity">
              <div className="mb-podcastKicker">EPISODE {String(sortedRuns.length - index).padStart(2, "0")}{index === 0 && <span> · LATEST</span>}</div>
              <h3>My Podcast</h3>
              <div className="mb-podcastMeta">
                {date > 0 && <time dateTime={new Date(date).toISOString()}>{new Date(date).toLocaleString()}</time>}
                <span title={runId}>Run {runId.slice(0, 8)}</span>
              </div>
            </div>
            <div className="mb-podcastHeaderActions">
              {audioUrl && <button className="mb-podcastShareButton" type="button" onClick={() => {
                setSharingRunId(run.id);
                trackPodcastAction("share_open", { builderId, runId: run.id, source: "builder" });
              }}><Share2 size={15} /> Share</button>}
              <span className={`mb-podcastStatus is-${displayStatus.kind}`} title={`Pipeline status: ${status}`}>
                {displayStatus.kind === "ready" ? <Check size={13} /> : displayStatus.kind === "running" ? <LoaderCircle size={13} className="isSpinning" /> : null}
                {displayStatus.label}
              </span>
            </div>
          </div>
          {run.error && <p className="mb-podcastsError" role="alert">{String(run.error)}</p>}
          <div className="mb-podcastFilesHeader"><span>Episode files</span><small>{readyCount} of {PODCAST_FILES.length} ready</small></div>
          <div className="mb-podcastFiles">
            <DownloadFileCard label="Original script" description="The script used to create this podcast" name="script.txt" url={fileUrls[run.id]?.scriptPath} />
            <DownloadFileCard label="Outline" description="Ready to download" name="script_outline.txt" url={fileUrls[run.id]?.outlinePath} />
          </div>
          <div className={`mb-podcastAudio ${audioUrl ? "isReady" : ""}`}>
            {audioUrl ? <audio className="mb-podcastPlayer" controls src={audioUrl} preload="metadata" aria-label="Podcast audio" onPlay={() => trackPodcastAction("play", { builderId, runId: run.id, source: "builder" })} /> :
              <div className="mb-podcastAudioPending"><Headphones size={18} />{displayStatus.kind === "failed" ? "Audio is not available for this run" : "Your audio will appear here when it’s ready"}</div>}
          </div>
          {displayStatus.kind === "running" && <p className="mb-podcastNote">Status updates automatically. Files appear as soon as they’re available.</p>}
        </article>;
      })}
    </div>
    {shareUrl && <PodcastShareDialog key={sharingRunId} url={shareUrl} builderId={builderId} runId={sharingRunId} onClose={() => setSharingRunId("")} />}
  </section>;
}
